import { App_Url_New } from "@/shared/common/static";
import ChatBotRender from "@/shared/components/ChatBotRender";
import FormLayout from "@/shared/components/FormLayout";
import { closeModal } from "@/shared/redux/reducers/uiSlice";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { getChatbotName } from "@/whiteLable";
import { Maximize2, Minimize2, X } from "lucide-react";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";

const Modal = () => {
  const [chatBotSize, setChatBotSize] = useState<"normal" | "expanded">("normal");
  const [selectedChatbotUrl, setSelectedChatbotUrl] = useState<string | null>(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const {
    ui: { status, name, title, children, className, header, outerClassName, form, data, url },
  } = usePosterReducers();

  // Snapshot the modal's content while open and freeze it during the close
  // animation — otherwise `closeModal()` clears `name`/`data` immediately,
  // which flips isChatBotModal/children mid-fade and flashes empty content.
  const [activeModal, setActiveModal] = useState({
    name,
    title,
    children,
    className,
    header,
    outerClassName,
    form,
    data,
    url,
  });

  useEffect(() => {
    if (status !== "hide") {
      setActiveModal({ name, title, children, className, header, outerClassName, form, data, url });
    }
  }, [status, name, title, children, className, header, outerClassName, form, data, url]);

  const onSubmit = activeModal.form?.onSubmit;
  const defaultValues = activeModal.form?.defaultValues;
  const submitText = activeModal.form?.submitText;
  const dispatch = useDispatch();
  const isForm = typeof onSubmit === "function";
  const isChatBotModal = activeModal.name === App_Url_New.PopupKeys.OPEN_CHAT_BOT;
  const chatBotUrl = (activeModal.data?.iframeUrl ||
    activeModal.data?.url ||
    activeModal.url ||
    "") as string;
  const chatbotOptions = (activeModal.data?.chatbotOptions || []) as Array<{
    label: string;
    url: string;
  }>;

  useEffect(() => {
    if (status === "hide") {
      setSelectedChatbotUrl(null);
      setChatBotSize("normal");
      return;
    }
  }, [status]);

  // Keep the modal mounted for the exit animation before actually removing it.
  useEffect(() => {
    if (status !== "hide") {
      setShouldRender(true);
      const raf = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(raf);
    }

    setIsVisible(false);
    const timeout = setTimeout(() => setShouldRender(false), 300);
    return () => clearTimeout(timeout);
  }, [status]);

  if (!shouldRender) return null;
  // ESC close
  //   useEffect(() => {
  //     const handleEsc = (e: KeyboardEvent) => {
  //       if (e.key === "Escape") {
  //         onClose(name);
  //       }
  //     };
  //     document.addEventListener("keydown", handleEsc);
  //     return () => document.removeEventListener("keydown", handleEsc);
  //   }, [onClose, name]);

  if (isChatBotModal) {
    const isExpanded = chatBotSize === "expanded";
    const sizeClass = isExpanded ? "w-[min(960px,100vw)]" : "w-[min(600px,100vw)]";
    const chatbotName = getChatbotName();

    return ReactDOM.createPortal(
      <div
        id={`modal-${activeModal.name}`}
        className="fixed inset-0 z-999 pointer-events-none"
        data-modal-name={activeModal.name}
      >
        <div
          role="dialog"
          aria-label={`${chatbotName} chat assistant`}
          className={`pointer-events-auto fixed right-0 top-16 h-[calc(100dvh-4rem)] overflow-hidden border-l border-gray-200 bg-theme-surface shadow-2xl transition-[width,transform,opacity] duration-300 ease-out ${sizeClass} ${isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}`}
        >
          <div className="absolute right-3 top-4 z-10 flex items-center gap-1">
            {chatbotOptions.length > 0 && selectedChatbotUrl ? (
              <button
                type="button"
                onClick={() => setSelectedChatbotUrl(null)}
                className="cursor-pointer rounded-full bg-black/40 px-3 py-1.5 text-xs font-medium text-gray-300 transition-colors hover:bg-black/60 hover:text-white"
              >
                Back
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setChatBotSize(isExpanded ? "normal" : "expanded")}
              aria-label={isExpanded ? "Restore chatbot size" : "Expand chatbot"}
              className="cursor-pointer rounded-full bg-black/40 p-1.5 text-gray-300 transition-colors hover:bg-black/60 hover:text-white"
            >
              {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={() => {
                setChatBotSize("normal");
                dispatch(closeModal());
              }}
              aria-label="Close chatbot"
              className="cursor-pointer rounded-full bg-black/40 p-1.5 text-gray-300 transition-colors hover:bg-black/60 hover:text-theme-danger"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {chatbotOptions.length > 0 && !selectedChatbotUrl ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-4 px-8 text-center text-white">
              <div>
                <h2 className="text-lg font-semibold">Choose chatbot dashboard</h2>
                <p className="mt-1 text-sm text-gray-400">Select the dashboard you want to open.</p>
              </div>
              <div className="flex w-full flex-col gap-3">
                {chatbotOptions.map((option) => (
                  <button
                    key={option.url}
                    type="button"
                    onClick={() => setSelectedChatbotUrl(option.url)}
                    className="rounded-lg border border-blue-400/40 bg-blue-500/15 px-4 py-3 font-medium text-blue-100 transition-colors hover:bg-blue-500/30"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full w-full">
              <ChatBotRender iframeUrl={selectedChatbotUrl || chatBotUrl} title={chatbotName} />
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div
      id={`modal-${activeModal.name}`}
      className="fixed inset-0 z-999"
      data-modal-name={activeModal.name}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-blue-500/5 backdrop-blur-sm transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}
        onClick={() => dispatch(closeModal())}
      />

      {/* Modal Wrapper */}
      <div
        className={`relative flex min-h-screen justify-center items-center ${activeModal.outerClassName}`}
      >
        <div
          className={`w-full max-h-[90vh] bg-white shadow-xl rounded-2xl flex flex-col p-3 transition-all duration-300 ease-out ${isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"} ${activeModal.className}`}
        >
          {/* Header */}
          <div className="mt-2 flex shrink-0 items-center justify-between px-5">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{activeModal.title}</h3>
            {activeModal.header && <div>{activeModal.header}</div>}
            <button
              onClick={() => dispatch(closeModal())}
              aria-label="Close"
              className="cursor-pointer text-gray-400 transition-all duration-200 ease-in-out hover:text-theme-danger hover:scale-110 hover:rotate-90 active:scale-95"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          {isForm ? (
            <FormLayout submitText={submitText} defaultValues={defaultValues} onSubmit={onSubmit}>
              <div className="px-5 py-4 text-gray-700 overflow-y-auto thin-scrollbar flex-1 min-h-0">
                {activeModal.children}
              </div>
            </FormLayout>
          ) : (
            <div className="px-5 py-4 text-gray-700 overflow-y-auto thin-scrollbar flex-1 min-h-0">
              {activeModal.children}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
